import { clearCustomerWidgetAuthToken } from "@redotech/redo-customer-portal-app/api";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { CustomerContext } from "@redotech/redo-customer-portal-app/contexts/CustomerContext";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import {
  SetUserContext,
  UserContext,
} from "@redotech/redo-customer-portal-app/contexts/user";
import { CustomizationContext } from "@redotech/redo-customer-portal-app/customization/customization-context";
import { resolveCustomizationImage } from "@redotech/redo-customer-portal-app/customization/resolve-customization-field";
import { useTranslation } from "@redotech/redo-i18n";
import { LocaleSelector } from "@redotech/redo-i18n/locale-selector";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { allowCustomerAccounts } from "@redotech/redo-web/utils/customer-account-utils";
import { assertNever } from "@redotech/util/type";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getUserDisplayEmail, getUserDisplayName } from "../utils/user-display";
import { NavbarFontContext } from "./navbar-font-context";
import { OrderContext } from "./order";
import { VersionFourNavbarDisplay } from "./version-four-navbar-display";

enum MenuOption {
  MyOrders = "My orders",
  MyAccount = "My account",
  Logout = "Logout",
  MyWarranties = "My warranties",
}

export function VersionFourNavbar() {
  const { t } = useTranslation();
  const settings = useContext(SettingsContext);
  const customerContext = useContext(CustomerContext);
  const { customization } = useContext(CustomizationContext);
  const { fontFamily: navbarFontFamily } = useContext(NavbarFontContext);
  const order = useContext(OrderContext);
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);

  const logoUrl = resolveCustomizationImage(
    customization?.branding?.logoUrl,
    settings?.theme?.logo_image,
  );

  const primaryEmail = getUserDisplayEmail(user, customerContext);
  const customerName = getUserDisplayName(user, customerContext);

  const orderName = order?.shopify?.name;

  const menuOptions: RedoListItem<MenuOption>[] = useMemo(() => {
    const optionToLabel: Record<MenuOption, string> = {
      [MenuOption.MyOrders]: t("My Orders"),
      [MenuOption.MyAccount]: t("My Account"),
      [MenuOption.Logout]: t("Logout"),
      [MenuOption.MyWarranties]: t("My Warranties"),
    };

    const myOrdersOption =
      user && !user?.guestEmail && user.email ? [MenuOption.MyOrders] : [];
    const myWarrantiesOption =
      user && !user?.guestEmail && user.email && settings?.warranties?.enabled
        ? [MenuOption.MyWarranties]
        : [];
    const myAccountOption =
      allowCustomerAccounts(settings?.customerAccounts) &&
      !!customerContext &&
      primaryEmail
        ? [MenuOption.MyAccount]
        : [];

    const menuOptions = [
      myOrdersOption,
      myWarrantiesOption,
      myAccountOption,
      MenuOption.Logout,
    ].flat();

    return menuOptions.map((option) => ({
      id: option,
      value: option,
      type: "text",
      text: optionToLabel[option],
    }));
  }, [user, settings, customerContext, primaryEmail, t]);

  if (settings?.hideTrackingPageNavbar) {
    return null;
  }

  if (!user || user?.isMerchant) {
    return null;
  }

  const storeDomain =
    settings?.brandKit?.customerFacingDomain || settings?.storeUrl;

  return (
    <VersionFourNavbarDisplay
      customerEmail={primaryEmail}
      customerName={customerName}
      fontFamily={navbarFontFamily}
      hideNavigation={settings?.hideTrackingPageNavigation}
      leftContent={<LocaleSelector />}
      logoHref={`https://${storeDomain}`}
      logoUrl={logoUrl}
      menuItems={menuOptions}
      onMenuItemSelected={(item) => {
        switch (item.value) {
          case MenuOption.MyOrders:
            navigate("/returns-portal/orders");
            break;
          case MenuOption.MyAccount:
            navigate("/returns-portal/account");
            break;
          case MenuOption.Logout:
            // @ts-expect-error: strict-null-checks
            setAuth_(undefined);
            // @ts-expect-error: strict-null-checks
            setUser(undefined);
            clearCustomerWidgetAuthToken();
            navigate("/returns-portal/login");
            break;
          case MenuOption.MyWarranties:
            navigate("/returns-portal/account/warranties");
            break;
          default:
            assertNever(item.value);
        }
      }}
      orderName={orderName}
      storeName={settings?.theme?.merchantDisplayName || settings?.merchantName}
    />
  );
}
